import { useEffect, useMemo, useRef, useState } from "react"
import { useData } from "../data/DataContext"
import {
  backfillReportingPeriodNames,
  createCategory,
  createDepartment,
  createPerson,
  createReportingPeriod,
  deleteCategory,
  deleteDepartment,
  deleteRagScore,
  deleteReportingPeriod,
  reassignPersonRefs,
  setCurrentReportingPeriod,
  updateObjective,
  updatePerson,
  updateRagScore,
  updateReportingPeriod,
} from "../data/api"
import type {
  Objective,
  Person,
  PersonFieldCreate,
  PersonFieldUpdate,
  RagScore,
  ReportingPeriod,
  ReportingPeriodFieldCreate,
  ReportingPeriodFieldUpdate,
  TaxonomyItem,
  Team,
} from "../data/types"

/**
 * Admin page.
 *
 *   Admin / Exec:  full access to all tabs
 *   CLT:           scoped Roster only (me + people I cover); can add Users
 *                  that auto-default to CoveredBy = the CLT signing in
 *   Others:        blocked by defence-in-depth gate
 *
 * Sub-tabs (full set):
 *   - Roster              (CRUD + CoveredBy people-picker)
 *   - Reporting periods   (Admin/Exec)
 *   - Taxonomy            (Categories + Departments; Admin/Exec)
 *   - Data health         (orphan cleanup; Admin/Exec)
 *   - Audit trail         (placeholder)
 */

type AdminTab = "roster" | "reporting-periods" | "taxonomy" | "data-health" | "audit"

const TABS: { id: AdminTab; label: string; soon: boolean }[] = [
  { id: "roster", label: "Roster", soon: false },
  { id: "reporting-periods", label: "Reporting periods", soon: false },
  { id: "taxonomy", label: "Taxonomy", soon: false },
  { id: "data-health", label: "Data health", soon: false },
  { id: "audit", label: "Audit trail", soon: true },
]

const TEAMS: Team[] = ["Exec", "CLT", "User"]

export function AdminPage() {
  const { me } = useData()

  // Role gate — Admin / Exec / CLT only.
  if (me.role !== "Admin" && me.role !== "Exec" && me.role !== "CLT") {
    return <NotAuthorised />
  }

  const isFullAdmin = me.role === "Admin" || me.role === "Exec"
  const visibleTabs = isFullAdmin ? TABS : TABS.filter((t) => t.id === "roster")

  const [tab, setTab] = useState<AdminTab>("roster")

  return (
    <div>
      {visibleTabs.length > 1 ? (
        <TabStrip current={tab} onChange={setTab} tabs={visibleTabs} />
      ) : null}
      {tab === "roster" && <RosterTab />}
      {isFullAdmin && tab === "reporting-periods" && <ReportingPeriodsTab />}
      {isFullAdmin && tab === "taxonomy" && <TaxonomyTab />}
      {isFullAdmin && tab === "data-health" && <DataHealthTab />}
      {isFullAdmin && tab === "audit" && <AuditTab />}
    </div>
  )
}

/* ---------- Not authorised ---------- */

function NotAuthorised() {
  return (
    <div className="rounded-lg border border-rag-amber bg-rag-amber-bg p-8 shadow-card">
      <div className="mb-2 font-mono text-[10px] uppercase tracking-[0.14em] text-rag-amber-ink">
        Not authorised
      </div>
      <h2 className="mb-3 font-display text-[22px] font-medium tracking-[-0.01em] text-ink">
        Admin area restricted
      </h2>
      <p className="max-w-xl text-[14px] leading-[1.6] text-ink-2">
        This area is for Admin, Exec and CLT roles. CLT members see a scoped
        roster (their direct covers only). If you believe this is in error,
        contact Chris Flynn.
      </p>
    </div>
  )
}

/* ---------- Tab strip ---------- */

function TabStrip({
  current,
  onChange,
  tabs,
}: {
  current: AdminTab
  onChange: (t: AdminTab) => void
  tabs: { id: AdminTab; label: string; soon: boolean }[]
}) {
  return (
    <div className="mb-6 flex flex-wrap items-end gap-0 border-b border-rule">
      {tabs.map((t) => {
        const active = current === t.id
        return (
          <button
            key={t.id}
            type="button"
            onClick={() => onChange(t.id)}
            className={[
              "relative px-[16px] pt-[8px] pb-[11px] font-mono text-[11px] uppercase tracking-[0.14em] transition",
              active
                ? "text-ink after:absolute after:inset-x-[12px] after:bottom-[-1px] after:h-[2px] after:bg-brass after:content-['']"
                : "text-ink-3 hover:text-ink-2",
            ].join(" ")}
          >
            {t.label}
            {t.soon ? (
              <span className="ml-[7px] rounded-full bg-paper-sunk px-[7px] py-[1px] text-[9px] tracking-[0.12em] text-ink-3">
                Soon
              </span>
            ) : null}
          </button>
        )
      })}
    </div>
  )
}

/* ---------- Roster tab ---------- */

function RosterTab() {
  const { people, me, refresh } = useData()
  const [editingPerson, setEditingPerson] = useState<Person | null>(null)
  const [adding, setAdding] = useState(false)
  const [bannerError, setBannerError] = useState<string | null>(null)

  const isFullAdmin = me.role === "Admin" || me.role === "Exec"

  // For CLT, scope the roster to the CLT + their direct covers.
  const visiblePeople = useMemo(() => {
    if (isFullAdmin) return people
    const self = me.matchedPerson
    const coverIds = new Set(me.covers.map((c) => c.id))
    return people.filter(
      (p) => (self != null && p.id === self.id) || coverIds.has(p.id),
    )
  }, [isFullAdmin, people, me.matchedPerson, me.covers])

  const sorted = useMemo(() => {
    const teamRank: Record<string, number> = { Exec: 0, CLT: 1 }
    return [...visiblePeople].sort((a, b) => {
      if (a.active !== b.active) return a.active ? -1 : 1
      const at = teamRank[a.team ?? ""] ?? 99
      const bt = teamRank[b.team ?? ""] ?? 99
      if (at !== bt) return at - bt
      const ad = a.department ?? ""
      const bd = b.department ?? ""
      if (ad !== bd) return ad.localeCompare(bd)
      return a.fullName.localeCompare(b.fullName)
    })
  }, [visiblePeople])

  const activeCount = visiblePeople.filter((p) => p.active).length
  const inactiveCount = visiblePeople.length - activeCount
  const coveredCount = visiblePeople.filter(
    (p) => p.active && p.coveredBy != null && p.coveredBy.trim() !== "",
  ).length

  const handleSaved = async () => {
    await refresh()
    setEditingPerson(null)
    setAdding(false)
  }

  return (
    <div>
      {/* Intro + Add button */}
      <div className="mb-5 flex flex-wrap items-end justify-between gap-4">
        <p className="max-w-2xl text-[14px] leading-[1.6] text-ink-2">
          {isFullAdmin ? (
            <>
              <span className="font-medium text-ink">{activeCount} active</span>,{" "}
              {inactiveCount} inactive · {coveredCount} with a covering colleague.
              Click the pencil on any row to edit. Renames cascade to all
              objectives and RAG scores automatically (FK-linked).
            </>
          ) : (
            <>
              <span className="font-medium text-ink">{activeCount} active</span>{" "}
              in your team · {inactiveCount} inactive. You're seeing yourself
              plus anyone whose <span className="font-medium">Covered by</span>
              {" "}is you. New people you add will default to Team =
              <span className="font-medium"> User</span> and CoveredBy =
              <span className="font-medium"> {me.matchedPerson?.fullName ?? "you"}</span>.
            </>
          )}
        </p>
        <button
          type="button"
          onClick={() => setAdding(true)}
          className="inline-flex items-center gap-2 rounded-sm bg-nymr-navy px-[14px] py-2 font-mono text-[11px] uppercase tracking-[0.14em] text-white transition hover:bg-nymr-navy-dark"
        >
          <svg viewBox="0 0 20 20" fill="none" className="h-3.5 w-3.5">
            <path
              d="M10 4v12M4 10h12"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
          {isFullAdmin ? "Add person" : "Add user"}
        </button>
      </div>

      {bannerError ? (
        <div className="mb-4 rounded-md border border-rag-red bg-rag-red-bg p-3 text-[12.5px] text-rag-red">
          <p className="font-medium">Operation failed</p>
          <p className="mt-1 whitespace-pre-wrap font-mono text-[11px]">
            {bannerError}
          </p>
        </div>
      ) : null}

      {/* Table */}
      <div className="overflow-hidden rounded-lg border border-rule bg-card shadow-card">
        <table className="min-w-full text-[13px]">
          <thead className="bg-paper-sunk text-left font-mono text-[10px] uppercase tracking-[0.12em] text-ink-3">
            <tr>
              <th className="px-[14px] py-[10px]">Name</th>
              <th className="px-[14px] py-[10px]">Team</th>
              <th className="px-[14px] py-[10px]">Department</th>
              <th className="px-[14px] py-[10px]">Job title</th>
              <th className="px-[14px] py-[10px]">Covered by</th>
              <th className="px-[14px] py-[10px]">Status</th>
              <th className="px-[14px] py-[10px] text-right">Edit</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-rule">
            {sorted.map((p) => (
              <tr
                key={p.id}
                className={[
                  "group transition",
                  p.active ? "" : "opacity-55",
                  "hover:bg-paper-sunk/50",
                ].join(" ")}
              >
                <td className="px-[14px] py-[10px] font-medium text-ink">
                  {p.fullName}
                </td>
                <td className="px-[14px] py-[10px] text-ink-2">
                  {p.team ? (
                    <span className="inline-flex items-center rounded-full bg-paper-sunk px-[8px] py-[1px] font-mono text-[10px] uppercase tracking-[0.12em]">
                      {p.team}
                    </span>
                  ) : (
                    "—"
                  )}
                </td>
                <td className="px-[14px] py-[10px] text-ink-2">
                  {p.department ?? "—"}
                </td>
                <td className="px-[14px] py-[10px] text-ink-2">
                  {p.jobTitle ?? "—"}
                </td>
                <td className="px-[14px] py-[10px] text-ink-2">
                  {p.coveredBy ? (
                    p.coveredBy
                  ) : (
                    <span className="text-ink-3">—</span>
                  )}
                </td>
                <td className="px-[14px] py-[10px]">
                  {p.active ? (
                    <span className="inline-flex items-center rounded-full bg-rag-green-bg px-[8px] py-[1px] font-mono text-[10px] uppercase tracking-[0.12em] text-rag-green">
                      Active
                    </span>
                  ) : (
                    <span className="inline-flex items-center rounded-full bg-rag-unset-bg px-[8px] py-[1px] font-mono text-[10px] uppercase tracking-[0.12em] text-ink-3">
                      Inactive
                    </span>
                  )}
                </td>
                <td className="px-[14px] py-[10px] text-right">
                  <button
                    type="button"
                    onClick={() => setEditingPerson(p)}
                    title={`Edit ${p.fullName}`}
                    className="inline-grid h-7 w-7 place-items-center rounded-sm text-ink-3 opacity-0 transition hover:bg-paper-sunk hover:text-brass group-hover:opacity-100"
                  >
                    <svg viewBox="0 0 20 20" fill="none" className="h-[14px] w-[14px]">
                      <path
                        d="m4 16 1-4L13 4l3 3-8 8-4 1Zm9-12 3 3"
                        stroke="currentColor"
                        strokeWidth="1.6"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                </td>
              </tr>
            ))}
            {sorted.length === 0 ? (
              <tr>
                <td
                  colSpan={7}
                  className="px-[14px] py-8 text-center font-mono text-[11px] uppercase tracking-[0.12em] text-ink-3"
                >
                  No people in the roster
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      {/* Modal — `key` forces a fresh form state whenever the target changes */}
      {(editingPerson || adding) && (
        <PersonModal
          key={editingPerson?.id ?? "new"}
          person={editingPerson}
          allPeople={people}
          me={me}
          cltMode={!isFullAdmin}
          cltName={me.matchedPerson?.fullName ?? null}
          onClose={() => {
            setEditingPerson(null)
            setAdding(false)
          }}
          onSaved={handleSaved}
          onError={(msg) => setBannerError(msg)}
        />
      )}
    </div>
  )
}

/* ---------- Person edit/add modal ---------- */

function PersonModal({
  person,
  allPeople,
  me,
  cltMode,
  cltName,
  onClose,
  onSaved,
  onError,
}: {
  person: Person | null // null = add mode; non-null = edit mode
  allPeople: Person[]
  me: ReturnType<typeof useData>["me"]
  cltMode: boolean
  cltName: string | null
  onClose: () => void
  onSaved: () => void | Promise<void>
  onError: (msg: string) => void
}) {
  const { departments } = useData()
  const isNew = person == null

  // For CLT adding a new person: default Team=User and CoveredBy=CLT's own name.
  // Team and CoveredBy fields are locked in this mode.
  const cltAddDefaults = cltMode && isNew

  const [fullName, setFullName] = useState(person?.fullName ?? "")
  const [team, setTeam] = useState<string>(
    person?.team ?? (cltAddDefaults ? "User" : ""),
  )
  const [department, setDepartment] = useState<string>(person?.department ?? "")
  const [jobTitle, setJobTitle] = useState(person?.jobTitle ?? "")
  const [coveredBy, setCoveredBy] = useState<string | null>(
    person?.coveredBy ?? (cltAddDefaults ? cltName : null),
  )
  const [active, setActive] = useState(person?.active ?? true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [confirmingDelete, setConfirmingDelete] = useState(false)

  // Can the signed-in user delete THIS person?
  //   - Edit mode only
  //   - Never self-delete
  //   - CLT can only delete people they cover
  //   - Person must currently be Active (already-inactive = no-op)
  const canDelete =
    !isNew &&
    person != null &&
    person.active &&
    me.matchedPerson?.id !== person.id &&
    (!cltMode || me.covers.some((c) => c.id === person.id))

  // Escape to close
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape" && !busy && !confirmingDelete) onClose()
    }
    document.addEventListener("keydown", onKey)
    return () => document.removeEventListener("keydown", onKey)
  }, [onClose, busy, confirmingDelete])

  const handleSave = async () => {
    const trimmedName = fullName.trim()
    if (!trimmedName) {
      setError("Full name is required")
      return
    }

    // Warn about rename when the FullName is changing. With FK schema the rename
    // is harmless (PersonId references stay valid), but the user may still want
    // to confirm before changing how someone appears in dashboards.
    if (!isNew && person && person.fullName !== trimmedName) {
      const confirmed = window.confirm(
        `Rename "${person.fullName}" to "${trimmedName}"?\n\n` +
          `All Objectives, RAG scores, and CoveredBy references update\n` +
          `automatically because they're FK-linked by PersonId.`,
      )
      if (!confirmed) return
    }

    setBusy(true)
    setError(null)

    try {
      if (isNew) {
        const fields: PersonFieldCreate = {
          FullName: trimmedName,
          Team: team || null,
          Department: department || null,
          JobTitle: jobTitle.trim() || null,
          CoveredBy: coveredBy || null,
          Active: active,
        }
        await createPerson(fields)
      } else if (person) {
        const fields: PersonFieldUpdate = {
          FullName: trimmedName,
          Team: team || null,
          Department: department || null,
          JobTitle: jobTitle.trim() || null,
          CoveredBy: coveredBy || null,
          Active: active,
        }
        await updatePerson(person.id, fields)
      }
      await onSaved()
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err)
      setError(msg)
      onError(msg)
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-ink/35 p-4"
        role="dialog"
        aria-modal="true"
      >
        <div className="w-full max-w-[540px] rounded-lg border border-rule bg-card shadow-[0_20px_40px_rgba(20,49,79,0.22)]">
          {/* Header */}
          <div className="flex items-start justify-between gap-3 border-b border-rule px-[22px] pt-[18px] pb-[14px]">
            <div className="min-w-0">
              <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-brass">
                {isNew ? "Add person" : "Edit person"}
              </div>
              <h2 className="mt-[4px] font-display text-[20px] font-medium tracking-[-0.01em] text-ink">
                {isNew ? "New roster entry" : person?.fullName || "—"}
              </h2>
            </div>
            {canDelete ? (
              <button
                type="button"
                disabled={busy}
                onClick={() => setConfirmingDelete(true)}
                title={`Delete ${person?.fullName}`}
                className="mt-[2px] inline-grid h-8 w-8 shrink-0 place-items-center rounded-sm border border-rule-strong bg-card text-ink-3 transition hover:border-rag-red hover:bg-rag-red-bg hover:text-rag-red disabled:opacity-50"
              >
                <svg viewBox="0 0 20 20" fill="none" className="h-[15px] w-[15px]">
                  <path
                    d="M6 6h8m-7 0v9a1 1 0 0 0 1 1h4a1 1 0 0 0 1-1V6M8 6V4a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v2M4 6h12"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            ) : null}
          </div>

          {/* Body */}
          <div className="flex flex-col gap-[14px] p-[22px]">
            <Field label="Full name" required>
              <input
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                disabled={busy}
                className="rounded-sm border border-rule-strong bg-card px-[10px] py-2 text-[13px] text-ink focus:border-nymr-navy focus:outline-none disabled:opacity-60"
              />
            </Field>

            <div className="grid grid-cols-2 gap-[14px]">
              <Field label="Team">
                <select
                  value={team}
                  onChange={(e) => setTeam(e.target.value)}
                  disabled={busy || cltAddDefaults}
                  className="rounded-sm border border-rule-strong bg-card px-[10px] py-2 text-[13px] text-ink focus:border-nymr-navy focus:outline-none disabled:opacity-60"
                >
                  <option value="">—</option>
                  {TEAMS.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Department">
                <select
                  value={department}
                  onChange={(e) => setDepartment(e.target.value)}
                  disabled={busy}
                  className="rounded-sm border border-rule-strong bg-card px-[10px] py-2 text-[13px] text-ink focus:border-nymr-navy focus:outline-none disabled:opacity-60"
                >
                  <option value="">—</option>
                  {departments.map((d) => (
                    <option key={d.id} value={d.name}>
                      {d.name}
                    </option>
                  ))}
                </select>
              </Field>
            </div>

            <Field label="Job title">
              <input
                type="text"
                value={jobTitle}
                onChange={(e) => setJobTitle(e.target.value)}
                disabled={busy}
                className="rounded-sm border border-rule-strong bg-card px-[10px] py-2 text-[13px] text-ink focus:border-nymr-navy focus:outline-none disabled:opacity-60"
              />
            </Field>

            <Field
              label="Covered by"
              hint={
                cltAddDefaults
                  ? "Locked — new users you add are covered by you."
                  : "The colleague who maintains this person's objectives when they can't."
              }
            >
              <CoveredByCombobox
                value={coveredBy}
                onChange={setCoveredBy}
                people={allPeople}
                excludeName={person?.fullName}
                disabled={busy || cltAddDefaults}
              />
            </Field>

            <label className="mt-[2px] inline-flex items-center gap-2 text-[13px] text-ink">
              <input
                type="checkbox"
                checked={active}
                onChange={(e) => setActive(e.target.checked)}
                disabled={busy}
                className="h-4 w-4 rounded border-rule-strong accent-nymr-navy"
              />
              <span className="font-medium">Active</span>
              <span className="text-[12px] text-ink-3">
                {active
                  ? "Included in dashboards & coverage counts."
                  : "Soft-deleted — hidden from dashboards but preserves history."}
              </span>
            </label>

            {error ? (
              <div className="rounded-md border border-rag-red bg-rag-red-bg p-3 text-[12.5px] text-rag-red">
                <p className="font-medium">Save failed</p>
                <p className="mt-1 whitespace-pre-wrap font-mono text-[11px]">
                  {error}
                </p>
              </div>
            ) : null}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-2 border-t border-rule bg-paper-sunk px-[22px] py-[12px]">
            <button
              type="button"
              disabled={busy}
              onClick={onClose}
              className="rounded-sm border border-rule-strong bg-card px-[14px] py-[7px] font-mono text-[10.5px] uppercase tracking-[0.12em] text-ink-2 transition hover:border-ink-3 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={busy || !fullName.trim()}
              onClick={handleSave}
              className="rounded-sm bg-nymr-navy px-[14px] py-[7px] font-mono text-[10.5px] uppercase tracking-[0.12em] text-white transition hover:bg-nymr-navy-dark disabled:opacity-50"
            >
              {busy ? "Saving…" : isNew ? "Add person" : "Save changes"}
            </button>
          </div>
        </div>
      </div>

      {confirmingDelete && person ? (
        <DeleteConfirmModal
          person={person}
          allPeople={allPeople}
          onCancel={() => setConfirmingDelete(false)}
          onDone={async () => {
            setConfirmingDelete(false)
            await onSaved()
          }}
          onError={onError}
        />
      ) : null}
    </>
  )
}

/* ---------- Delete confirmation modal ---------- */

/**
 * Second-stage modal opened from PersonModal's delete icon.
 *
 * Two paths:
 *   - "Delete only": soft-delete the person (Active=false). Objectives and
 *     RagScores are left alone — they'll appear as orphans on Data health
 *     until the user resolves them manually.
 *   - "Reassign + delete": bulk-PATCH every non-deleted Objective and every
 *     RagScore referencing this person to the chosen replacement's name,
 *     then soft-delete the original. The new owner inherits the full RAG
 *     trend line.
 *
 * The reassign target picker excludes the person being deleted and any
 * already-inactive rows (CoveredByCombobox handles that filter).
 */
function DeleteConfirmModal({
  person,
  allPeople,
  onCancel,
  onDone,
  onError,
}: {
  person: Person
  allPeople: Person[]
  onCancel: () => void
  onDone: () => void | Promise<void>
  onError: (msg: string) => void
}) {
  const { objectives, ragScores } = useData()
  const [target, setTarget] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Counts for this person. Only non-deleted objectives are reassignable;
  // deleted ones stay with the original owner for history.
  const objectiveCount = useMemo(
    () =>
      objectives.filter(
        (o) => o.personName === person.fullName && o.status !== "Deleted",
      ).length,
    [objectives, person.fullName],
  )
  const ragCount = useMemo(
    () => ragScores.filter((r) => r.personName === person.fullName).length,
    [ragScores, person.fullName],
  )

  // Esc to cancel (only when not mid-operation)
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape" && !busy) onCancel()
    }
    document.addEventListener("keydown", onKey)
    return () => document.removeEventListener("keydown", onKey)
  }, [onCancel, busy])

  const runDelete = async (reassignTo: string | null) => {
    setBusy(true)
    setError(null)
    try {
      if (reassignTo) {
        const trimmed = reassignTo.trim()
        if (trimmed === person.fullName) {
          throw new Error("Can't reassign a person's work to themselves")
        }
        // Confirm target is still an active person (defence against stale state)
        const active = allPeople.some(
          (p) => p.active && p.fullName === trimmed,
        )
        if (!active) {
          throw new Error(`"${trimmed}" is not an active person`)
        }
        await reassignPersonRefs(person.fullName, trimmed, objectives, ragScores)
      }
      await updatePerson(person.id, { Active: false })
      await onDone()
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err)
      setError(msg)
      onError(msg)
    } finally {
      setBusy(false)
    }
  }

  const hasWork = objectiveCount > 0 || ragCount > 0
  const canReassign = target != null && target.trim() !== "" && !busy

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-ink/45 p-4"
      role="dialog"
      aria-modal="true"
    >
      <div className="w-full max-w-[520px] rounded-lg border border-rag-red/40 bg-card shadow-[0_24px_48px_rgba(20,49,79,0.28)]">
        {/* Header */}
        <div className="border-b border-rule px-[22px] pt-[18px] pb-[14px]">
          <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-rag-red">
            Delete user
          </div>
          <h2 className="mt-[4px] font-display text-[20px] font-medium tracking-[-0.01em] text-ink">
            Delete {person.fullName}?
          </h2>
          <p className="mt-[8px] text-[13px] leading-[1.55] text-ink-2">
            This is a <span className="font-medium text-ink">soft delete</span>{" "}
            — the row stays in the database with Active = false so history is
            preserved. You can reactivate them later by editing and ticking
            Active.
          </p>
        </div>

        {/* Body */}
        <div className="flex flex-col gap-[16px] p-[22px]">
          {/* Work summary */}
          <div className="rounded-md border border-rule bg-paper-sunk/60 px-[14px] py-[10px] text-[12.5px] leading-[1.55] text-ink-2">
            {hasWork ? (
              <>
                <span className="font-medium text-ink">
                  {objectiveCount} active objective
                  {objectiveCount === 1 ? "" : "s"}
                </span>{" "}
                and{" "}
                <span className="font-medium text-ink">
                  {ragCount} RAG score{ragCount === 1 ? "" : "s"}
                </span>{" "}
                reference {person.fullName}. If you reassign, both move to the
                new owner and the new owner inherits the full trend line. If
                you don't, the references stay pointing at{" "}
                {person.fullName.split(" ")[0]} and you can clean them up later
                from Data health.
              </>
            ) : (
              <>
                {person.fullName} has no objectives or RAG scores — safe to
                delete without reassignment.
              </>
            )}
          </div>

          {/* Reassign target picker */}
          {hasWork ? (
            <Field
              label="Reassign to (optional)"
              hint="Active colleagues only. Leave blank to delete without reassigning."
            >
              <CoveredByCombobox
                value={target}
                onChange={setTarget}
                people={allPeople}
                excludeName={person.fullName}
                disabled={busy}
              />
            </Field>
          ) : null}

          {error ? (
            <div className="rounded-md border border-rag-red bg-rag-red-bg p-3 text-[12.5px] text-rag-red">
              <p className="font-medium">Delete failed</p>
              <p className="mt-1 whitespace-pre-wrap font-mono text-[11px]">
                {error}
              </p>
            </div>
          ) : null}
        </div>

        {/* Footer */}
        <div className="flex flex-wrap items-center justify-end gap-2 border-t border-rule bg-paper-sunk px-[22px] py-[12px]">
          <button
            type="button"
            disabled={busy}
            onClick={onCancel}
            className="rounded-sm border border-rule-strong bg-card px-[14px] py-[7px] font-mono text-[10.5px] uppercase tracking-[0.12em] text-ink-2 transition hover:border-ink-3 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => void runDelete(null)}
            className="rounded-sm border border-rag-red bg-card px-[14px] py-[7px] font-mono text-[10.5px] uppercase tracking-[0.12em] text-rag-red transition hover:bg-rag-red-bg disabled:opacity-50"
          >
            {busy ? "Working…" : "Delete only"}
          </button>
          {hasWork ? (
            <button
              type="button"
              disabled={!canReassign}
              onClick={() => void runDelete(target)}
              className="rounded-sm bg-rag-red px-[14px] py-[7px] font-mono text-[10.5px] uppercase tracking-[0.12em] text-white transition hover:brightness-95 disabled:opacity-50"
            >
              {busy ? "Working…" : "Reassign + delete"}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  )
}

/* ---------- Labelled field wrapper ---------- */

function Field({
  label,
  required,
  hint,
  children,
}: {
  label: string
  required?: boolean
  hint?: string
  children: React.ReactNode
}) {
  return (
    <div className="flex flex-col gap-[6px]">
      <div className="flex items-baseline justify-between gap-2">
        <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-ink-3">
          {label}
          {required ? <span className="ml-1 text-brass">*</span> : null}
        </span>
        {hint ? <span className="text-[11px] text-ink-3">{hint}</span> : null}
      </div>
      {children}
    </div>
  )
}

/* ---------- CoveredBy combobox ---------- */

function CoveredByCombobox({
  value,
  onChange,
  people,
  excludeName,
  disabled,
}: {
  value: string | null
  onChange: (v: string | null) => void
  people: Person[]
  excludeName?: string
  disabled?: boolean
}) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState("")
  const containerRef = useRef<HTMLDivElement>(null)

  // Display value: the typed query while the dropdown is open; otherwise the
  // committed value (falling back to empty string so the placeholder shows).
  const displayValue = open ? query : value ?? ""

  const candidates = useMemo(() => {
    const excludeNorm = (excludeName ?? "").trim().toLowerCase()
    return people
      .filter(
        (p) =>
          p.active &&
          (excludeNorm === "" || p.fullName.trim().toLowerCase() !== excludeNorm),
      )
      .sort((a, b) => a.fullName.localeCompare(b.fullName))
  }, [people, excludeName])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return candidates
    return candidates.filter((p) => p.fullName.toLowerCase().includes(q))
  }, [candidates, query])

  // Close dropdown on outside click
  useEffect(() => {
    if (!open) return
    function onDown(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener("mousedown", onDown)
    return () => document.removeEventListener("mousedown", onDown)
  }, [open])

  // Flag if current value doesn't match any active person (orphan/inactive)
  const currentIsOrphan = useMemo(() => {
    if (!value) return false
    const v = value.trim().toLowerCase()
    return !people.some((p) => p.active && p.fullName.trim().toLowerCase() === v)
  }, [value, people])

  return (
    <div ref={containerRef} className="relative">
      <div className="flex items-stretch gap-1">
        <input
          type="text"
          value={displayValue}
          placeholder="No cover — type to search"
          disabled={disabled}
          onChange={(e) => {
            setQuery(e.target.value)
            setOpen(true)
          }}
          onFocus={() => {
            if (disabled) return
            setQuery(value ?? "")
            setOpen(true)
          }}
          className="min-w-0 flex-1 rounded-sm border border-rule-strong bg-card px-[10px] py-2 text-[13px] text-ink focus:border-nymr-navy focus:outline-none disabled:opacity-60"
        />
        {value ? (
          <button
            type="button"
            disabled={disabled}
            onClick={() => {
              onChange(null)
              setQuery("")
              setOpen(false)
            }}
            title="Clear cover"
            className="grid w-9 place-items-center rounded-sm border border-rule-strong bg-card text-[16px] leading-none text-ink-3 transition hover:border-ink-3 hover:text-ink-2 disabled:opacity-60"
          >
            ×
          </button>
        ) : null}
      </div>

      {currentIsOrphan && !open ? (
        <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.12em] text-rag-amber-ink">
          This name isn't an active person. Click × to clear, or pick a replacement.
        </div>
      ) : null}

      {open ? (
        <div className="absolute left-0 right-0 top-full z-30 mt-1 max-h-[240px] overflow-y-auto rounded-sm border border-rule-strong bg-card shadow-[0_10px_30px_rgba(20,49,79,0.15)]">
          {filtered.length > 0 ? (
            filtered.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => {
                  onChange(p.fullName)
                  setOpen(false)
                }}
                className="flex w-full items-baseline justify-between gap-2 px-[12px] py-[7px] text-left hover:bg-paper-sunk"
              >
                <span className="text-[13px] text-ink">{p.fullName}</span>
                {p.department ? (
                  <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-ink-3">
                    {p.department}
                  </span>
                ) : null}
              </button>
            ))
          ) : (
            <div className="px-[12px] py-[7px] text-[12.5px] italic text-ink-3">
              No active people match
            </div>
          )}
        </div>
      ) : null}
    </div>
  )
}

/* ---------- Reporting periods tab ---------- */

function formatDisplayDate(s: string | null): string {
  if (!s) return "\u2014"
  const d = new Date(s + "T00:00:00Z")
  if (isNaN(d.getTime())) return s
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  })
}

function ReportingPeriodsTab() {
  const { reportingPeriods, refresh } = useData()
  const [editing, setEditing] = useState<ReportingPeriod | null>(null)
  const [adding, setAdding] = useState(false)
  const [bannerError, setBannerError] = useState<string | null>(null)
  const [busyId, setBusyId] = useState<number | null>(null)

  const sorted = useMemo(() => {
    return [...reportingPeriods].sort((a, b) => {
      // Current first, then by StartDate descending (nulls last)
      if (a.isCurrent !== b.isCurrent) return a.isCurrent ? -1 : 1
      if (!a.startDate && b.startDate) return 1
      if (a.startDate && !b.startDate) return -1
      if (a.startDate && b.startDate)
        return b.startDate.localeCompare(a.startDate)
      return a.title.localeCompare(b.title)
    })
  }, [reportingPeriods])

  const currentPeriods = reportingPeriods.filter((p) => p.isCurrent)
  const multipleCurrent = currentPeriods.length > 1
  const current = currentPeriods[0] ?? null

  const handleDelete = async (p: ReportingPeriod) => {
    const ok = window.confirm(
      `Delete "${p.title}"?\n\n` +
        `RagScores with ReportingPeriodName="${p.title}" won't be updated — they'll keep the old name until cleaned up from Data health.`,
    )
    if (!ok) return
    setBusyId(p.id)
    setBannerError(null)
    try {
      await deleteReportingPeriod(p.id)
      await refresh()
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err)
      setBannerError(msg)
    } finally {
      setBusyId(null)
    }
  }

  const handleSaved = async () => {
    await refresh()
    setEditing(null)
    setAdding(false)
  }

  return (
    <div>
      {/* Intro + Add button */}
      <div className="mb-5 flex flex-wrap items-end justify-between gap-4">
        <p className="max-w-2xl text-[14px] leading-[1.6] text-ink-2">
          <span className="font-medium text-ink">
            {reportingPeriods.length} period{reportingPeriods.length === 1 ? "" : "s"}
          </span>{" "}
          ·{" "}
          {current ? (
            <>
              Current is{" "}
              <span className="font-medium text-ink">{current.title}</span>
            </>
          ) : (
            <span className="text-rag-amber-ink">No period marked as current</span>
          )}
          . Periods set the board-meeting cadence and tag each RAG score with
          its period.
        </p>
        <button
          type="button"
          onClick={() => setAdding(true)}
          className="inline-flex items-center gap-2 rounded-sm bg-nymr-navy px-[14px] py-2 font-mono text-[11px] uppercase tracking-[0.14em] text-white transition hover:bg-nymr-navy-dark"
        >
          <svg viewBox="0 0 20 20" fill="none" className="h-3.5 w-3.5">
            <path
              d="M10 4v12M4 10h12"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
          Add period
        </button>
      </div>

      {multipleCurrent ? (
        <div className="mb-3 rounded-md border border-rag-amber bg-rag-amber-bg p-3 text-[12.5px] text-rag-amber-ink">
          More than one period is marked Current. Open any row and re-save with
          Current ticked to resolve — others will be cleared automatically.
        </div>
      ) : null}

      {bannerError ? (
        <div className="mb-4 rounded-md border border-rag-red bg-rag-red-bg p-3 text-[12.5px] text-rag-red">
          <p className="font-medium">Operation failed</p>
          <p className="mt-1 whitespace-pre-wrap font-mono text-[11px]">
            {bannerError}
          </p>
        </div>
      ) : null}

      {/* Table */}
      <div className="overflow-hidden rounded-lg border border-rule bg-card shadow-card">
        <table className="min-w-full text-[13px]">
          <thead className="bg-paper-sunk text-left font-mono text-[10px] uppercase tracking-[0.12em] text-ink-3">
            <tr>
              <th className="px-[14px] py-[10px]">Title</th>
              <th className="px-[14px] py-[10px]">Year</th>
              <th className="px-[14px] py-[10px]">Start</th>
              <th className="px-[14px] py-[10px]">End</th>
              <th className="px-[14px] py-[10px]">Board meeting</th>
              <th className="px-[14px] py-[10px]">Deadline</th>
              <th className="px-[14px] py-[10px]">Current</th>
              <th className="px-[14px] py-[10px] text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-rule">
            {sorted.map((p) => (
              <tr
                key={p.id}
                className="group transition hover:bg-paper-sunk/50"
              >
                <td className="px-[14px] py-[10px] font-medium text-ink">
                  {p.title || <span className="text-ink-3">(untitled)</span>}
                </td>
                <td className="px-[14px] py-[10px] text-ink-2">
                  {p.year ?? "—"}
                </td>
                <td className="px-[14px] py-[10px] text-ink-2">
                  {formatDisplayDate(p.startDate)}
                </td>
                <td className="px-[14px] py-[10px] text-ink-2">
                  {formatDisplayDate(p.endDate)}
                </td>
                <td className="px-[14px] py-[10px] text-ink-2">
                  {formatDisplayDate(p.boardMeetingDate)}
                </td>
                <td className="px-[14px] py-[10px] text-ink-2">
                  {formatDisplayDate(p.boardDeadlineDate)}
                </td>
                <td className="px-[14px] py-[10px]">
                  {p.isCurrent ? (
                    <span className="inline-flex items-center gap-[4px] rounded-full bg-brass/15 px-[8px] py-[1px] font-mono text-[10px] uppercase tracking-[0.12em] text-brass">
                      ★ Current
                    </span>
                  ) : null}
                </td>
                <td className="px-[14px] py-[10px] text-right">
                  <div className="inline-flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => setEditing(p)}
                      title={`Edit ${p.title}`}
                      className="inline-grid h-7 w-7 place-items-center rounded-sm text-ink-3 opacity-0 transition hover:bg-paper-sunk hover:text-brass group-hover:opacity-100"
                    >
                      <svg
                        viewBox="0 0 20 20"
                        fill="none"
                        className="h-[14px] w-[14px]"
                      >
                        <path
                          d="m4 16 1-4L13 4l3 3-8 8-4 1Zm9-12 3 3"
                          stroke="currentColor"
                          strokeWidth="1.6"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                    <button
                      type="button"
                      disabled={busyId === p.id}
                      onClick={() => handleDelete(p)}
                      title={`Delete ${p.title}`}
                      className="inline-grid h-7 w-7 place-items-center rounded-sm text-ink-3 opacity-0 transition hover:bg-rag-red-bg hover:text-rag-red group-hover:opacity-100 disabled:opacity-50"
                    >
                      <svg
                        viewBox="0 0 20 20"
                        fill="none"
                        className="h-[14px] w-[14px]"
                      >
                        <path
                          d="M6 6h8m-7 0v9a1 1 0 0 0 1 1h4a1 1 0 0 0 1-1V6M8 6V4a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v2M4 6h12"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {sorted.length === 0 ? (
              <tr>
                <td
                  colSpan={8}
                  className="px-[14px] py-8 text-center font-mono text-[11px] uppercase tracking-[0.12em] text-ink-3"
                >
                  No reporting periods yet
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {(editing || adding) && (
        <ReportingPeriodModal
          key={editing?.id ?? "new"}
          period={editing}
          allPeriods={reportingPeriods}
          onClose={() => {
            setEditing(null)
            setAdding(false)
          }}
          onSaved={handleSaved}
          onError={(msg) => setBannerError(msg)}
        />
      )}
    </div>
  )
}

/* ---------- Reporting period modal ---------- */

function ReportingPeriodModal({
  period,
  allPeriods,
  onClose,
  onSaved,
  onError,
}: {
  period: ReportingPeriod | null
  allPeriods: ReportingPeriod[]
  onClose: () => void
  onSaved: () => void | Promise<void>
  onError: (msg: string) => void
}) {
  const isNew = period == null

  const [title, setTitle] = useState(period?.title ?? "")
  const [year, setYear] = useState<string>(
    period?.year != null
      ? String(period.year)
      : String(new Date().getFullYear()),
  )
  const [startDate, setStartDate] = useState(period?.startDate ?? "")
  const [endDate, setEndDate] = useState(period?.endDate ?? "")
  const [boardMeetingDate, setBoardMeetingDate] = useState(
    period?.boardMeetingDate ?? "",
  )
  const [boardDeadlineDate, setBoardDeadlineDate] = useState(
    period?.boardDeadlineDate ?? "",
  )
  const [isCurrent, setIsCurrent] = useState(period?.isCurrent ?? false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Esc to close
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape" && !busy) onClose()
    }
    document.addEventListener("keydown", onKey)
    return () => document.removeEventListener("keydown", onKey)
  }, [onClose, busy])

  const handleSave = async () => {
    const trimmedTitle = title.trim()
    if (!trimmedTitle) {
      setError("Title is required")
      return
    }
    if (startDate && endDate && endDate < startDate) {
      setError("End date must be on or after the start date")
      return
    }
    if (
      boardDeadlineDate &&
      boardMeetingDate &&
      boardDeadlineDate > boardMeetingDate
    ) {
      setError("Board deadline must be on or before the board meeting")
      return
    }

    let yearNum: number | null = null
    if (year.trim() !== "") {
      const n = Number(year)
      if (!Number.isFinite(n) || n < 1900 || n > 2200) {
        setError("Year must be a sensible integer")
        return
      }
      yearNum = Math.trunc(n)
    }

    setBusy(true)
    setError(null)

    try {
      if (isNew) {
        // Create as non-current first, then exclusively promote if requested,
        // so the flag is only ever true on one row at a time.
        const createFields: ReportingPeriodFieldCreate = {
          Title: trimmedTitle,
          Year: yearNum,
          StartDate: startDate || null,
          EndDate: endDate || null,
          BoardMeetingDate: boardMeetingDate || null,
          BoardDeadlineDate: boardDeadlineDate || null,
          IsCurrent: false,
        }
        const newId = await createReportingPeriod(createFields)
        if (isCurrent) {
          const synthetic: ReportingPeriod = {
            id: newId,
            title: trimmedTitle,
            year: yearNum,
            startDate: startDate || null,
            endDate: endDate || null,
            boardMeetingDate: boardMeetingDate || null,
            boardDeadlineDate: boardDeadlineDate || null,
            isCurrent: false,
          }
          await setCurrentReportingPeriod(newId, [...allPeriods, synthetic])
        }
      } else if (period) {
        const updateFields: ReportingPeriodFieldUpdate = {
          Title: trimmedTitle,
          Year: yearNum,
          StartDate: startDate || null,
          EndDate: endDate || null,
          BoardMeetingDate: boardMeetingDate || null,
          BoardDeadlineDate: boardDeadlineDate || null,
        }
        if (isCurrent && !period.isCurrent) {
          await updateReportingPeriod(period.id, updateFields)
          await setCurrentReportingPeriod(period.id, allPeriods)
        } else if (!isCurrent && period.isCurrent) {
          await updateReportingPeriod(period.id, {
            ...updateFields,
            IsCurrent: false,
          })
        } else {
          await updateReportingPeriod(period.id, updateFields)
        }
      }
      await onSaved()
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err)
      setError(msg)
      onError(msg)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink/35 p-4"
      role="dialog"
      aria-modal="true"
    >
      <div className="w-full max-w-[560px] rounded-lg border border-rule bg-card shadow-[0_20px_40px_rgba(20,49,79,0.22)]">
        {/* Header */}
        <div className="border-b border-rule px-[22px] pt-[18px] pb-[14px]">
          <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-brass">
            {isNew ? "Add reporting period" : "Edit reporting period"}
          </div>
          <h2 className="mt-[4px] font-display text-[20px] font-medium tracking-[-0.01em] text-ink">
            {isNew ? "New period" : period?.title || "—"}
          </h2>
        </div>

        {/* Body */}
        <div className="flex flex-col gap-[14px] p-[22px]">
          <Field label="Title" required hint="E.g. Mar–Apr 2026">
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              disabled={busy}
              className="rounded-sm border border-rule-strong bg-card px-[10px] py-2 text-[13px] text-ink focus:border-nymr-navy focus:outline-none disabled:opacity-60"
            />
          </Field>

          <div className="grid grid-cols-3 gap-[14px]">
            <Field label="Year">
              <input
                type="number"
                value={year}
                onChange={(e) => setYear(e.target.value)}
                disabled={busy}
                min={1900}
                max={2200}
                className="rounded-sm border border-rule-strong bg-card px-[10px] py-2 text-[13px] text-ink focus:border-nymr-navy focus:outline-none disabled:opacity-60"
              />
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-[14px]">
            <Field label="Start date">
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                disabled={busy}
                className="rounded-sm border border-rule-strong bg-card px-[10px] py-2 text-[13px] text-ink focus:border-nymr-navy focus:outline-none disabled:opacity-60"
              />
            </Field>
            <Field label="End date">
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                disabled={busy}
                className="rounded-sm border border-rule-strong bg-card px-[10px] py-2 text-[13px] text-ink focus:border-nymr-navy focus:outline-none disabled:opacity-60"
              />
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-[14px]">
            <Field label="Board meeting">
              <input
                type="date"
                value={boardMeetingDate}
                onChange={(e) => setBoardMeetingDate(e.target.value)}
                disabled={busy}
                className="rounded-sm border border-rule-strong bg-card px-[10px] py-2 text-[13px] text-ink focus:border-nymr-navy focus:outline-none disabled:opacity-60"
              />
            </Field>
            <Field
              label="Board deadline"
              hint="Content cut-off before the meeting"
            >
              <input
                type="date"
                value={boardDeadlineDate}
                onChange={(e) => setBoardDeadlineDate(e.target.value)}
                disabled={busy}
                className="rounded-sm border border-rule-strong bg-card px-[10px] py-2 text-[13px] text-ink focus:border-nymr-navy focus:outline-none disabled:opacity-60"
              />
            </Field>
          </div>

          <label className="mt-[2px] flex items-start gap-2 text-[13px] text-ink">
            <input
              type="checkbox"
              checked={isCurrent}
              onChange={(e) => setIsCurrent(e.target.checked)}
              disabled={busy}
              className="mt-[3px] h-4 w-4 rounded border-rule-strong accent-nymr-navy"
            />
            <span className="flex flex-col">
              <span className="font-medium">Mark as current reporting period</span>
              <span className="mt-[2px] text-[12px] text-ink-3">
                {isCurrent && period?.isCurrent !== true
                  ? "Any other period marked Current will be cleared automatically."
                  : "Only one period can be Current at a time."}
              </span>
            </span>
          </label>

          {error ? (
            <div className="rounded-md border border-rag-red bg-rag-red-bg p-3 text-[12.5px] text-rag-red">
              <p className="font-medium">Save failed</p>
              <p className="mt-1 whitespace-pre-wrap font-mono text-[11px]">
                {error}
              </p>
            </div>
          ) : null}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 border-t border-rule bg-paper-sunk px-[22px] py-[12px]">
          <button
            type="button"
            disabled={busy}
            onClick={onClose}
            className="rounded-sm border border-rule-strong bg-card px-[14px] py-[7px] font-mono text-[10.5px] uppercase tracking-[0.12em] text-ink-2 transition hover:border-ink-3 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={busy || !title.trim()}
            onClick={handleSave}
            className="rounded-sm bg-nymr-navy px-[14px] py-[7px] font-mono text-[10.5px] uppercase tracking-[0.12em] text-white transition hover:bg-nymr-navy-dark disabled:opacity-50"
          >
            {busy ? "Saving…" : isNew ? "Add period" : "Save changes"}
          </button>
        </div>
      </div>
    </div>
  )
}

/* ---------- Data health tab ---------- */

function normaliseName(s: string): string {
  return s.trim().toLowerCase().replace(/\s+/g, " ")
}

function DataHealthTab() {
  const { people, objectives, ragScores, reportingPeriods, categories, refresh } =
    useData()
  const [busyKey, setBusyKey] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  const clearStatus = () => {
    setError(null)
    setSuccess(null)
  }

  const activeNames = useMemo(() => {
    const s = new Set<string>()
    for (const p of people) if (p.active) s.add(normaliseName(p.fullName))
    return s
  }, [people])

  const allPersonNames = useMemo(() => {
    const s = new Set<string>()
    for (const p of people) s.add(normaliseName(p.fullName))
    return s
  }, [people])

  /** For each person (by normalised name), the set of their current non-deleted objective categories. */
  const objectiveCategoriesByPerson = useMemo(() => {
    const m = new Map<string, Set<string>>()
    for (const o of objectives) {
      if (o.status === "Deleted" || !o.category) continue
      const key = normaliseName(o.personName)
      let cats = m.get(key)
      if (!cats) {
        cats = new Set<string>()
        m.set(key, cats)
      }
      cats.add(o.category)
    }
    return m
  }, [objectives])

  /**
   * Live objectives that share a (Person, Category, Year) slot with another.
   *
   * RagScores are keyed (PersonId, CategoryId, Year, Month) and carry no
   * ObjectiveId, so the CATEGORY is what identifies an objective for
   * scoring. Two live objectives in one category therefore read and write
   * the SAME score row each month - each one's RAG value and narrative
   * overwrites the other's, and only one of them is ever displayed. Worse,
   * recategorising one of them leaves the shared history behind, where it
   * is silently re-presented as the remaining objective's own history.
   *
   * The API now refuses to create or move an objective into an occupied
   * slot (409), so this list can only contain pre-existing collisions.
   * Clearing it also unblocks the UX_Objectives_PersonCategoryYear_Live
   * constraint in db/04_objective_slot_uniqueness.sql.
   */
  const duplicateSlotGroups = useMemo(() => {
    const groups = new Map<string, Objective[]>()
    for (const o of objectives) {
      if (o.status === "Deleted" || !o.category || o.year == null) continue
      const key = `${normaliseName(o.personName)}|${o.category.trim()}|${o.year}`
      const arr = groups.get(key)
      if (arr) arr.push(o)
      else groups.set(key, [o])
    }
    return [...groups.values()]
      .filter((g) => g.length > 1)
      .sort(
        (a, b) =>
          a[0].personName.localeCompare(b[0].personName) ||
          (a[0].category ?? "").localeCompare(b[0].category ?? ""),
      )
  }, [objectives])

  const duplicateSlotCount = useMemo(
    () => duplicateSlotGroups.reduce((n, g) => n + g.length, 0),
    [duplicateSlotGroups],
  )

  const categoryNames = useMemo(
    () => categories.map((c) => c.name).sort(),
    [categories],
  )

  const objOrphans = useMemo(
    () =>
      objectives
        .filter(
          (o) =>
            o.status !== "Deleted" &&
            !activeNames.has(normaliseName(o.personName)),
        )
        .sort(
          (a, b) =>
            a.personName.localeCompare(b.personName) ||
            (a.category ?? "").localeCompare(b.category ?? ""),
        ),
    [objectives, activeNames],
  )

  const ragPersonOrphans = useMemo(
    () =>
      ragScores
        .filter((r) => !allPersonNames.has(normaliseName(r.personName)))
        .sort(
          (a, b) =>
            a.personName.localeCompare(b.personName) ||
            a.category.localeCompare(b.category) ||
            a.year - b.year ||
            a.month - b.month,
        ),
    [ragScores, allPersonNames],
  )

  const ragCategoryOrphans = useMemo(
    () =>
      ragScores
        .filter((r) => {
          // Skip person orphans — handled separately
          if (!allPersonNames.has(normaliseName(r.personName))) return false
          const cats = objectiveCategoriesByPerson.get(
            normaliseName(r.personName),
          )
          return !cats || !cats.has(r.category)
        })
        .sort(
          (a, b) =>
            a.personName.localeCompare(b.personName) ||
            a.category.localeCompare(b.category) ||
            a.year - b.year ||
            a.month - b.month,
        ),
    [ragScores, allPersonNames, objectiveCategoriesByPerson],
  )

  /** RagScores whose (year, month) falls inside a ReportingPeriod window but ReportingPeriodName is missing. */
  const backfillCandidates = useMemo(() => {
    const usable = reportingPeriods.filter(
      (p) => p.startDate && p.endDate && p.title,
    )
    if (usable.length === 0) return [] as RagScore[]
    const out: RagScore[] = []
    for (const s of ragScores) {
      if (s.reportingPeriodName && s.reportingPeriodName.trim() !== "") continue
      if (s.month < 1 || s.month > 12) continue
      const calendarYear = s.month >= 4 ? s.year : s.year + 1
      const scoreDate = `${calendarYear}-${String(s.month).padStart(2, "0")}-15`
      const match = usable.some(
        (p) => p.startDate! <= scoreDate && scoreDate <= p.endDate!,
      )
      if (match) out.push(s)
    }
    return out
  }, [ragScores, reportingPeriods])

  const totalMissingPeriodName = useMemo(
    () =>
      ragScores.filter(
        (s) => !s.reportingPeriodName || s.reportingPeriodName.trim() === "",
      ).length,
    [ragScores],
  )

  /* ----- Action handlers ----- */

  const handleMarkObjectiveDeleted = async (o: Objective) => {
    clearStatus()
    setBusyKey(`obj-${o.id}`)
    try {
      await updateObjective(o.id, { Status: "Deleted" })
      await refresh()
      setSuccess(
        `Marked "${o.category ?? "(no category)"}" for ${o.personName} as Deleted.`,
      )
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusyKey(null)
    }
  }

  const handleBulkMarkObjectivesDeleted = async () => {
    if (objOrphans.length === 0) return
    const ok = window.confirm(
      `Mark all ${objOrphans.length} orphan objective${objOrphans.length === 1 ? "" : "s"} as Deleted?\n\n` +
        `Reversible by editing each one and changing Status back.`,
    )
    if (!ok) return
    clearStatus()
    setBusyKey("bulk-obj")
    try {
      await Promise.all(
        objOrphans.map((o) => updateObjective(o.id, { Status: "Deleted" })),
      )
      await refresh()
      setSuccess(
        `Marked ${objOrphans.length} orphan objective${objOrphans.length === 1 ? "" : "s"} as Deleted.`,
      )
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusyKey(null)
    }
  }

  const handleDeleteRagScore = async (s: RagScore, confirmPrompt = true) => {
    if (confirmPrompt) {
      const ok = window.confirm(
        `Delete RAG score for ${s.personName} · ${s.category} (${s.month}/${s.year})?`,
      )
      if (!ok) return
    }
    clearStatus()
    setBusyKey(`rag-${s.id}`)
    try {
      await deleteRagScore(s.id)
      await refresh()
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusyKey(null)
    }
  }

  const handleBulkDeleteRagPersonOrphans = async () => {
    if (ragPersonOrphans.length === 0) return
    const ok = window.confirm(
      `Permanently delete ${ragPersonOrphans.length} RAG score${ragPersonOrphans.length === 1 ? "" : "s"} with unmatched PersonName?\n\n` +
        `This cannot be undone from the app.`,
    )
    if (!ok) return
    clearStatus()
    setBusyKey("bulk-rag-person")
    try {
      await Promise.all(ragPersonOrphans.map((s) => deleteRagScore(s.id)))
      await refresh()
      setSuccess(
        `Deleted ${ragPersonOrphans.length} orphan RAG score${ragPersonOrphans.length === 1 ? "" : "s"}.`,
      )
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusyKey(null)
    }
  }

  /**
   * Move one objective out of a contested slot into a free category. The API
   * refuses a move into an occupied slot (409) and decides what happens to
   * the RAG history: it follows the objective when the old category is left
   * empty, but stays put when another live objective remains there, because
   * a shared history belongs to the objective that stays.
   */
  const handleMoveObjectiveCategory = async (
    o: Objective,
    newCategory: string,
  ) => {
    if (!newCategory || newCategory === o.category) return
    clearStatus()
    setBusyKey(`objmove-${o.id}`)
    try {
      const result = await updateObjective(o.id, { Category: newCategory })
      await refresh()
      const where = `from "${o.category}" to "${newCategory}"`
      let tail: string
      if (result?.scoresRetainedBySlotMate) {
        tail =
          `Its RAG history stayed on "${o.category}" with the other objective there — ` +
          `the two were sharing it, so it belongs to the one that stayed. ` +
          `Score this objective fresh under "${newCategory}".`
      } else if (result && result.scoresMoved > 0) {
        tail =
          `${result.scoresMoved} month${result.scoresMoved === 1 ? "" : "s"} of RAG history moved with it` +
          (result.scoresLeftBehind > 0
            ? `; ${result.scoresLeftBehind} stayed behind because "${newCategory}" was already scored for those months.`
            : ".")
      } else {
        tail = "It had no RAG history to move."
      }
      setSuccess(`Moved ${o.personName}'s objective ${where}. ${tail}`)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusyKey(null)
    }
  }

  const handleRenameRagCategory = async (s: RagScore, newCategory: string) => {
    if (!newCategory || newCategory === s.category) return
    clearStatus()
    setBusyKey(`rag-${s.id}`)
    try {
      await updateRagScore(s.id, { Category: newCategory })
      await refresh()
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusyKey(null)
    }
  }

  const handleBackfill = async () => {
    if (backfillCandidates.length === 0) return
    clearStatus()
    setBusyKey("backfill")
    try {
      const n = await backfillReportingPeriodNames(ragScores, reportingPeriods)
      await refresh()
      setSuccess(
        `Back-filled ReportingPeriodName on ${n} score${n === 1 ? "" : "s"}.`,
      )
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusyKey(null)
    }
  }

  return (
    <div className="flex flex-col gap-5">
      {/* Summary cards */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <StatCard
          label="Duplicate objective slots"
          count={duplicateSlotCount}
          subtle="Objectives sharing one category — their RAG history collides"
        />
        <StatCard
          label="Objective orphans"
          count={objOrphans.length}
          subtle="PersonName doesn't match an active Person"
        />
        <StatCard
          label="RAG person orphans"
          count={ragPersonOrphans.length}
          subtle="RagScore PersonName doesn't match any Person"
        />
        <StatCard
          label="RAG category orphans"
          count={ragCategoryOrphans.length}
          subtle="Category doesn't match a current objective for that person"
        />
        <StatCard
          label="Missing period names"
          count={totalMissingPeriodName}
          subtle={`${backfillCandidates.length} can be back-filled from ReportingPeriods`}
        />
      </div>

      {/* Status banners */}
      {success ? (
        <div className="rounded-md border border-rag-green bg-rag-green-bg p-3 text-[12.5px] text-rag-green">
          <p className="font-medium">Done</p>
          <p className="mt-1">{success}</p>
        </div>
      ) : null}
      {error ? (
        <div className="rounded-md border border-rag-red bg-rag-red-bg p-3 text-[12.5px] text-rag-red">
          <p className="font-medium">Operation failed</p>
          <p className="mt-1 whitespace-pre-wrap font-mono text-[11px]">{error}</p>
        </div>
      ) : null}

      {/* Section: Duplicate objective slots */}
      <HealthSection
        title="Duplicate objective slots"
        subtitle={
          "Two or more live objectives share one (person, category, year). RAG scores are stored " +
          "against the CATEGORY, not the objective, so these objectives overwrite each other's " +
          "monthly score and narrative — and only one of them is ever shown. Fix by moving one to " +
          "a free category, or marking it Deleted. The RAG history already recorded here stays " +
          "with the objective that remains — the two were sharing it — so score the moved one " +
          "fresh under its new category."
        }
        count={duplicateSlotCount}
      >
        {duplicateSlotGroups.length === 0 ? (
          <AllClear />
        ) : (
          <HealthTable
            headers={["Person", "Category", "Year", "Objective", ""]}
            align={["left", "left", "left", "left", "right"]}
          >
            {duplicateSlotGroups.flatMap((group) => {
              const taken = new Set(
                objectiveCategoriesByPerson.get(
                  normaliseName(group[0].personName),
                ) ?? [],
              )
              const freeCats = categoryNames.filter((c) => !taken.has(c))
              return group.map((o, i) => {
                const rowBusy = busyKey === `objmove-${o.id}` || busyKey === `obj-${o.id}`
                return (
                  <tr
                    key={o.id}
                    className={[
                      "hover:bg-paper-sunk/50",
                      i === 0 ? "border-t-2 border-rule-strong" : "",
                    ].join(" ")}
                  >
                    <td className="px-[14px] py-[8px] font-medium text-ink">
                      {i === 0 ? o.personName : ""}
                    </td>
                    <td className="px-[14px] py-[8px] text-ink-2">
                      {i === 0 ? o.category : ""}
                    </td>
                    <td className="px-[14px] py-[8px] text-ink-2">
                      {i === 0 ? o.year : ""}
                    </td>
                    <td className="px-[14px] py-[8px] text-ink-2">
                      <span className="line-clamp-2 max-w-[42ch] text-[12px]">
                        {o.objectiveText?.trim() || "—"}
                      </span>
                    </td>
                    <td className="px-[14px] py-[8px] text-right">
                      <div className="inline-flex items-center gap-1">
                        {freeCats.length > 0 ? (
                          <select
                            value=""
                            disabled={rowBusy}
                            onChange={(e) =>
                              handleMoveObjectiveCategory(o, e.target.value)
                            }
                            className="rounded-sm border border-rule-strong bg-card px-[8px] py-[3px] font-mono text-[10px] uppercase tracking-[0.1em] text-ink-2 disabled:opacity-50"
                          >
                            <option value="" disabled>
                              Move to…
                            </option>
                            {freeCats.map((c) => (
                              <option key={c} value={c}>
                                {c}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-ink-3">
                            No free category
                          </span>
                        )}
                        <button
                          type="button"
                          disabled={rowBusy}
                          onClick={() => handleMarkObjectiveDeleted(o)}
                          className="rounded-sm border border-rule-strong bg-card px-[10px] py-[4px] font-mono text-[10px] uppercase tracking-[0.12em] text-ink-2 transition hover:border-rag-red hover:text-rag-red disabled:opacity-50"
                        >
                          {rowBusy ? "…" : "Mark Deleted"}
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              })
            })}
          </HealthTable>
        )}
      </HealthSection>

      {/* Section: Objective orphans */}
      <HealthSection
        title="Objective orphans"
        subtitle="Active objectives whose PersonName doesn't match any active Person. Usually the person has left or been deactivated. Safe fix: mark Deleted."
        count={objOrphans.length}
        bulkAction={
          objOrphans.length > 0
            ? {
                label: `Mark all ${objOrphans.length} as Deleted`,
                onClick: handleBulkMarkObjectivesDeleted,
                busy: busyKey === "bulk-obj",
              }
            : null
        }
      >
        {objOrphans.length === 0 ? (
          <AllClear />
        ) : (
          <HealthTable
            headers={["Person", "Category", "Year", "Status", ""]}
            align={["left", "left", "left", "left", "right"]}
          >
            {objOrphans.map((o) => (
              <tr key={o.id} className="hover:bg-paper-sunk/50">
                <td className="px-[14px] py-[8px] font-medium text-ink">
                  {o.personName}
                </td>
                <td className="px-[14px] py-[8px] text-ink-2">
                  {o.category ?? "—"}
                </td>
                <td className="px-[14px] py-[8px] text-ink-2">
                  {o.year ?? "—"}
                </td>
                <td className="px-[14px] py-[8px] text-ink-2">
                  {o.status ?? "—"}
                </td>
                <td className="px-[14px] py-[8px] text-right">
                  <button
                    type="button"
                    disabled={busyKey === `obj-${o.id}`}
                    onClick={() => handleMarkObjectiveDeleted(o)}
                    className="rounded-sm border border-rule-strong bg-card px-[10px] py-[4px] font-mono text-[10px] uppercase tracking-[0.12em] text-ink-2 transition hover:border-rag-red hover:text-rag-red disabled:opacity-50"
                  >
                    {busyKey === `obj-${o.id}` ? "…" : "Mark Deleted"}
                  </button>
                </td>
              </tr>
            ))}
          </HealthTable>
        )}
      </HealthSection>

      {/* Section: RAG person orphans */}
      <HealthSection
        title="RAG person orphans"
        subtitle="RAG scores whose PersonName doesn't match any Person row (active or inactive). Safe fix: delete them — they're not tied to anything."
        count={ragPersonOrphans.length}
        bulkAction={
          ragPersonOrphans.length > 0
            ? {
                label: `Delete all ${ragPersonOrphans.length}`,
                onClick: handleBulkDeleteRagPersonOrphans,
                busy: busyKey === "bulk-rag-person",
                danger: true,
              }
            : null
        }
      >
        {ragPersonOrphans.length === 0 ? (
          <AllClear />
        ) : (
          <HealthTable
            headers={["Person", "Category", "Year", "Month", "RAG", ""]}
            align={["left", "left", "left", "left", "left", "right"]}
          >
            {ragPersonOrphans.map((s) => (
              <tr key={s.id} className="hover:bg-paper-sunk/50">
                <td className="px-[14px] py-[8px] font-medium text-ink">
                  {s.personName}
                </td>
                <td className="px-[14px] py-[8px] text-ink-2">{s.category}</td>
                <td className="px-[14px] py-[8px] text-ink-2">{s.year}</td>
                <td className="px-[14px] py-[8px] text-ink-2">{s.month}</td>
                <td className="px-[14px] py-[8px]">
                  <RagChip value={s.ragValue} />
                </td>
                <td className="px-[14px] py-[8px] text-right">
                  <button
                    type="button"
                    disabled={busyKey === `rag-${s.id}`}
                    onClick={() => handleDeleteRagScore(s)}
                    className="rounded-sm border border-rule-strong bg-card px-[10px] py-[4px] font-mono text-[10px] uppercase tracking-[0.12em] text-ink-2 transition hover:border-rag-red hover:text-rag-red disabled:opacity-50"
                  >
                    {busyKey === `rag-${s.id}` ? "…" : "Delete"}
                  </button>
                </td>
              </tr>
            ))}
          </HealthTable>
        )}
      </HealthSection>

      {/* Section: RAG category orphans */}
      <HealthSection
        title="RAG category orphans"
        subtitle="RAG scores whose Category doesn't match any current objective for that person. Usually left over after a category rename. Fix per row: reassign to a current category, or delete."
        count={ragCategoryOrphans.length}
      >
        {ragCategoryOrphans.length === 0 ? (
          <AllClear />
        ) : (
          <HealthTable
            headers={["Person", "Orphan category", "Year", "Month", "RAG", "Fix"]}
            align={["left", "left", "left", "left", "left", "right"]}
          >
            {ragCategoryOrphans.map((s) => {
              const validCats = [
                ...(objectiveCategoriesByPerson.get(
                  normaliseName(s.personName),
                ) ?? new Set<string>()),
              ].sort()
              const rowBusy = busyKey === `rag-${s.id}`
              return (
                <tr key={s.id} className="hover:bg-paper-sunk/50">
                  <td className="px-[14px] py-[8px] font-medium text-ink">
                    {s.personName}
                  </td>
                  <td className="px-[14px] py-[8px] text-ink-2">
                    {s.category}
                  </td>
                  <td className="px-[14px] py-[8px] text-ink-2">{s.year}</td>
                  <td className="px-[14px] py-[8px] text-ink-2">{s.month}</td>
                  <td className="px-[14px] py-[8px]">
                    <RagChip value={s.ragValue} />
                  </td>
                  <td className="px-[14px] py-[8px] text-right">
                    <div className="inline-flex items-center gap-1">
                      {validCats.length > 0 ? (
                        <select
                          value=""
                          disabled={rowBusy}
                          onChange={(e) =>
                            handleRenameRagCategory(s, e.target.value)
                          }
                          className="rounded-sm border border-rule-strong bg-card px-[8px] py-[3px] font-mono text-[10px] uppercase tracking-[0.1em] text-ink-2 disabled:opacity-50"
                        >
                          <option value="" disabled>
                            Rename to…
                          </option>
                          {validCats.map((c) => (
                            <option key={c} value={c}>
                              {c}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-ink-3">
                          No current objectives
                        </span>
                      )}
                      <button
                        type="button"
                        disabled={rowBusy}
                        onClick={() => handleDeleteRagScore(s)}
                        className="rounded-sm border border-rule-strong bg-card px-[10px] py-[4px] font-mono text-[10px] uppercase tracking-[0.12em] text-ink-2 transition hover:border-rag-red hover:text-rag-red disabled:opacity-50"
                      >
                        {rowBusy ? "…" : "Delete"}
                      </button>
                    </div>
                  </td>
                </tr>
              )
            })}
          </HealthTable>
        )}
      </HealthSection>

      {/* Section: Reporting period name back-fill */}
      <HealthSection
        title="Reporting period back-fill"
        subtitle="Tag each RAG score with the ReportingPeriod it belongs to by matching (year, month) against the period's date range. Run any time — already-tagged scores are skipped."
        count={backfillCandidates.length}
        bulkAction={
          backfillCandidates.length > 0
            ? {
                label: `Back-fill ${backfillCandidates.length} score${backfillCandidates.length === 1 ? "" : "s"}`,
                onClick: handleBackfill,
                busy: busyKey === "backfill",
              }
            : null
        }
      >
        <div className="px-[14px] py-[10px] text-[12.5px] text-ink-2">
          {reportingPeriods.length === 0 ? (
            <span className="text-rag-amber-ink">
              No reporting periods defined yet — add some under the
              <span className="font-medium"> Reporting periods </span>tab first.
            </span>
          ) : backfillCandidates.length === 0 ? (
            totalMissingPeriodName > 0 ? (
              <span>
                {totalMissingPeriodName} score
                {totalMissingPeriodName === 1 ? "" : "s"} still missing a period
                name, but none falls inside any defined period's window.
                Extend the date ranges under the
                <span className="font-medium"> Reporting periods </span>tab,
                then re-run.
              </span>
            ) : (
              <span>All RAG scores already tagged with a period name.</span>
            )
          ) : (
            <span>
              Ready to tag{" "}
              <span className="font-medium text-ink">
                {backfillCandidates.length}
              </span>{" "}
              score{backfillCandidates.length === 1 ? "" : "s"}.
            </span>
          )}
        </div>
      </HealthSection>
    </div>
  )
}

/* ---------- Data health helpers ---------- */

function HealthSection({
  title,
  subtitle,
  count,
  bulkAction,
  children,
}: {
  title: string
  subtitle: string
  count: number
  bulkAction?: {
    label: string
    onClick: () => void
    busy: boolean
    danger?: boolean
  } | null
  children: React.ReactNode
}) {
  const countTone =
    count === 0
      ? "bg-rag-green-bg text-rag-green"
      : "bg-rag-amber-bg text-rag-amber-ink"
  return (
    <section className="overflow-hidden rounded-lg border border-rule bg-card shadow-card">
      <header className="flex flex-wrap items-start justify-between gap-3 border-b border-rule px-[18px] py-[14px]">
        <div className="min-w-0">
          <div className="flex items-baseline gap-2">
            <h3 className="font-display text-[16px] font-medium tracking-[-0.005em] text-ink">
              {title}
            </h3>
            <span
              className={[
                "inline-flex items-center rounded-full px-[8px] py-[1px] font-mono text-[10px] uppercase tracking-[0.12em]",
                countTone,
              ].join(" ")}
            >
              {count}
            </span>
          </div>
          <p className="mt-[4px] max-w-3xl text-[12.5px] leading-[1.5] text-ink-2">
            {subtitle}
          </p>
        </div>
        {bulkAction ? (
          <button
            type="button"
            disabled={bulkAction.busy}
            onClick={bulkAction.onClick}
            className={[
              "shrink-0 rounded-sm px-[12px] py-[6px] font-mono text-[10.5px] uppercase tracking-[0.12em] text-white transition disabled:opacity-50",
              bulkAction.danger
                ? "bg-rag-red hover:brightness-95"
                : "bg-nymr-navy hover:bg-nymr-navy-dark",
            ].join(" ")}
          >
            {bulkAction.busy ? "Working…" : bulkAction.label}
          </button>
        ) : null}
      </header>
      <div>{children}</div>
    </section>
  )
}

function HealthTable({
  headers,
  align,
  children,
}: {
  headers: string[]
  align: Array<"left" | "right">
  children: React.ReactNode
}) {
  return (
    <table className="min-w-full text-[13px]">
      <thead className="bg-paper-sunk text-left font-mono text-[10px] uppercase tracking-[0.12em] text-ink-3">
        <tr>
          {headers.map((h, i) => (
            <th
              key={i}
              className={[
                "px-[14px] py-[8px]",
                align[i] === "right" ? "text-right" : "text-left",
              ].join(" ")}
            >
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody className="divide-y divide-rule">{children}</tbody>
    </table>
  )
}

function AllClear() {
  return (
    <div className="flex items-center gap-2 px-[14px] py-[12px] text-[12.5px] text-ink-2">
      <span className="inline-flex h-[18px] w-[18px] items-center justify-center rounded-full bg-rag-green-bg text-rag-green">
        ✓
      </span>
      <span>All clear.</span>
    </div>
  )
}

function RagChip({ value }: { value: 1 | 2 | 3 | null }) {
  const { label, cls } =
    value === 1
      ? { label: "Red", cls: "bg-rag-red text-white" }
      : value === 2
        ? { label: "Amber", cls: "bg-rag-amber text-ink" }
        : value === 3
          ? { label: "Green", cls: "bg-rag-green text-white" }
          : { label: "—", cls: "bg-rag-unset text-ink-3" }
  return (
    <span
      className={[
        "inline-flex items-center rounded-full px-[8px] py-[1px] font-mono text-[9px] uppercase tracking-[0.1em]",
        cls,
      ].join(" ")}
    >
      {label}
    </span>
  )
}

function StatCard({
  label,
  count,
  subtle,
}: {
  label: string
  count: number
  subtle: string
}) {
  return (
    <div className="rounded-md border border-rule bg-paper-sunk p-4">
      <div className="font-mono text-[10px] uppercase tracking-[0.12em] text-ink-3">
        {label}
      </div>
      <div
        className={[
          "mt-2 font-display text-[28px] font-medium tracking-[-0.01em]",
          count > 0 ? "text-rag-amber-ink" : "text-ink",
        ].join(" ")}
      >
        {count}
      </div>
      <div className="mt-1 text-[11.5px] leading-[1.4] text-ink-3">{subtle}</div>
    </div>
  )
}

/* ---------- Audit trail tab ---------- */

function AuditTab() {
  return (
    <div className="rounded-lg border border-rule bg-card p-8 shadow-card">
      <div className="mb-2 font-mono text-[10px] uppercase tracking-[0.14em] text-brass">
        Phase 7+
      </div>
      <h2 className="mb-3 font-display text-[22px] font-medium tracking-[-0.01em] text-ink">
        Audit trail
      </h2>
      <p className="max-w-xl text-[14px] leading-[1.6] text-ink-2">
        The database row is updated on every save with the user's name and timestamp.
        This page will surface a searchable activity feed on top of that — who
        changed what, when — once the underlying audit table lands.
      </p>
    </div>
  )
}

/* ---------- Taxonomy tab (Categories + Departments) ---------- */

function TaxonomyTab() {
  const { categories, departments, objectives, people, refresh } = useData()

  // In-use counts — used to block deletion if a category/department is referenced.
  const categoryUsage = useMemo(() => {
    const m = new Map<string, number>()
    for (const o of objectives) {
      if (o.status === "Deleted" || !o.category) continue
      m.set(o.category, (m.get(o.category) ?? 0) + 1)
    }
    return m
  }, [objectives])

  const departmentUsage = useMemo(() => {
    const m = new Map<string, number>()
    for (const p of people) {
      if (!p.active || !p.department) continue
      m.set(p.department, (m.get(p.department) ?? 0) + 1)
    }
    return m
  }, [people])

  return (
    <div className="flex flex-col gap-5">
      <p className="max-w-3xl text-[14px] leading-[1.6] text-ink-2">
        Pickers across the app pull from these two lists — the Category dropdown
        on objectives, the Department dropdown on people. Adds here are visible
        everywhere after the next refresh. Items in use can't be deleted until
        nothing references them.
      </p>

      <TaxonomySection
        title="Categories"
        subtitle="Columns in the Team dashboard heatmap and the Category filter on All objectives."
        items={categories}
        usage={categoryUsage}
        usageUnit="objective"
        onAdd={async (name) => {
          await createCategory(name)
          await refresh()
        }}
        onDelete={async (id) => {
          await deleteCategory(id)
          await refresh()
        }}
      />

      <TaxonomySection
        title="Departments"
        subtitle="Grouping used in the Team dashboard, Reports, and the Department dropdown on people."
        items={departments}
        usage={departmentUsage}
        usageUnit="person"
        onAdd={async (name) => {
          await createDepartment(name)
          await refresh()
        }}
        onDelete={async (id) => {
          await deleteDepartment(id)
          await refresh()
        }}
      />
    </div>
  )
}

function TaxonomySection({
  title,
  subtitle,
  items,
  usage,
  usageUnit,
  onAdd,
  onDelete,
}: {
  title: string
  subtitle: string
  items: TaxonomyItem[]
  usage: Map<string, number>
  usageUnit: "objective" | "person"
  onAdd: (name: string) => Promise<void>
  onDelete: (id: number) => Promise<void>
}) {
  const [adding, setAdding] = useState("")
  const [busyKey, setBusyKey] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const handleAdd = async () => {
    const name = adding.trim()
    if (!name) return
    // Prevent duplicates (case-insensitive)
    const clash = items.find((i) => i.name.toLowerCase() === name.toLowerCase())
    if (clash) {
      setError(`"${clash.name}" already exists`)
      return
    }
    setError(null)
    setBusyKey("add")
    try {
      await onAdd(name)
      setAdding("")
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusyKey(null)
    }
  }

  const handleDelete = async (item: TaxonomyItem) => {
    const count = usage.get(item.name) ?? 0
    if (count > 0) {
      setError(
        `Can't delete "${item.name}" — ${count} ${usageUnit}${count === 1 ? "" : "s"} still reference it.`,
      )
      return
    }
    const ok = window.confirm(`Delete "${item.name}"? This cannot be undone.`)
    if (!ok) return
    setError(null)
    setBusyKey(`del-${item.id}`)
    try {
      await onDelete(item.id)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusyKey(null)
    }
  }

  return (
    <section className="overflow-hidden rounded-lg border border-rule bg-card shadow-card">
      <header className="border-b border-rule px-[18px] py-[14px]">
        <div className="flex items-baseline gap-2">
          <h3 className="font-display text-[16px] font-medium tracking-[-0.005em] text-ink">
            {title}
          </h3>
          <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-ink-3">
            {items.length} {items.length === 1 ? "item" : "items"}
          </span>
        </div>
        <p className="mt-[4px] max-w-3xl text-[12.5px] leading-[1.5] text-ink-2">
          {subtitle}
        </p>
      </header>

      {/* Add row */}
      <div className="flex flex-wrap items-center gap-2 border-b border-rule bg-paper-sunk/30 px-[14px] py-[10px]">
        <input
          type="text"
          value={adding}
          onChange={(e) => setAdding(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault()
              void handleAdd()
            }
          }}
          placeholder={`New ${title.slice(0, -1).toLowerCase()} name…`}
          disabled={busyKey === "add"}
          className="min-w-[240px] flex-1 rounded-sm border border-rule-strong bg-card px-[10px] py-[6px] text-[13px] text-ink focus:border-nymr-navy focus:outline-none disabled:opacity-60"
        />
        <button
          type="button"
          onClick={() => void handleAdd()}
          disabled={busyKey === "add" || !adding.trim()}
          className="rounded-sm bg-nymr-navy px-[12px] py-[6px] font-mono text-[10.5px] uppercase tracking-[0.12em] text-white transition hover:bg-nymr-navy-dark disabled:opacity-50"
        >
          {busyKey === "add" ? "Adding…" : "Add"}
        </button>
      </div>

      {error ? (
        <div className="border-b border-rule bg-rag-red-bg px-[14px] py-[8px] text-[12.5px] text-rag-red">
          {error}
        </div>
      ) : null}

      {/* List */}
      {items.length === 0 ? (
        <p className="px-[14px] py-[14px] text-[12.5px] italic text-ink-3">
          Nothing here yet — add your first {title.slice(0, -1).toLowerCase()} above.
        </p>
      ) : (
        <table className="min-w-full text-[13px]">
          <thead className="bg-paper-sunk text-left font-mono text-[10px] uppercase tracking-[0.12em] text-ink-3">
            <tr>
              <th className="px-[14px] py-[8px]">Name</th>
              <th className="px-[14px] py-[8px]">In use by</th>
              <th className="px-[14px] py-[8px] text-right">Delete</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-rule">
            {items.map((item) => {
              const count = usage.get(item.name) ?? 0
              const rowBusy = busyKey === `del-${item.id}`
              return (
                <tr key={item.id} className="group hover:bg-paper-sunk/50">
                  <td className="px-[14px] py-[8px] font-medium text-ink">
                    {item.name}
                  </td>
                  <td className="px-[14px] py-[8px] text-ink-2">
                    {count === 0 ? (
                      <span className="text-ink-3">—</span>
                    ) : (
                      <>
                        {count} {usageUnit}
                        {count === 1 ? "" : "s"}
                      </>
                    )}
                  </td>
                  <td className="px-[14px] py-[8px] text-right">
                    <button
                      type="button"
                      disabled={rowBusy || count > 0}
                      title={
                        count > 0
                          ? `In use by ${count} ${usageUnit}${count === 1 ? "" : "s"}`
                          : `Delete ${item.name}`
                      }
                      onClick={() => void handleDelete(item)}
                      className="inline-grid h-7 w-7 place-items-center rounded-sm text-ink-3 opacity-0 transition hover:bg-rag-red-bg hover:text-rag-red group-hover:opacity-100 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-ink-3"
                    >
                      <svg viewBox="0 0 20 20" fill="none" className="h-[14px] w-[14px]">
                        <path
                          d="M6 6h8m-7 0v9a1 1 0 0 0 1 1h4a1 1 0 0 0 1-1V6M8 6V4a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v2M4 6h12"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      )}
    </section>
  )
}
